import React, { useState } from "react"
import { StripeCardForm } from "./StripeCardForm"
import { CheckoutOrder } from "../../../models/order"
import { useElements, useStripe } from "@stripe/react-stripe-js"
import { logCheckoutFailed } from "../../../context/analytics-handlers"
import { useTranslation } from "react-i18next"
import { translationKeys } from "../../../util/translationKeys"
import { formatMoneyAmount } from "../../../util/price-util"
import { useI18next } from "gatsby-plugin-react-i18next"
import { usePaymentService } from "../../../hooks/use-payment-service"
import SSESubscriberService from "../../../services/SSESubscriberService"
import { getErrorMessage } from "../../../util/error-handler"
import HeapsError from "../../../util/heaps-error"
import { StripeNextAction } from "../../../models/payment-methods"
import { captureException } from "../../../services/exception-logger"
import { trackPaymentStage } from "../../../services/payment-telemetry"

type Props = {
  nextAction: StripeNextAction
  hide: () => void
  showPaymentFailedScreen: (
    errorMessage?: string,
    providerName?: string
  ) => void
  showPaymentSucceededScreen: () => void
  paymentProcessing: boolean
  sseSubscriber: React.MutableRefObject<SSESubscriberService | undefined>
  setPaymentProcessing: React.Dispatch<React.SetStateAction<boolean>>
  startTimeout: (preCancelAction: () => void) => void
  setTimeoutId: (val: NodeJS.Timeout | undefined) => void
  timeoutId: React.MutableRefObject<NodeJS.Timeout | undefined>
  order: CheckoutOrder
}

export const StripeCardPaymentForm = ({
  hide,
  nextAction,
  startTimeout,
  setTimeoutId,
  timeoutId,
  paymentProcessing,
  setPaymentProcessing,
  sseSubscriber,
  showPaymentFailedScreen,
  showPaymentSucceededScreen,
  order,
}: Props) => {
  const [userActionsDone, setUserActionsDone] = useState(false)
  const stripe = useStripe()
  const elements = useElements()
  const { performStripeSCA, handleCancelPayment } = usePaymentService()
  const { language } = useI18next()
  const { t } = useTranslation()

  const onBackClicked = async () => {
    if (paymentProcessing) {
      return
    }
    sseSubscriber?.current?.close()
    try {
      await handleCancelPayment(order.order.id, showPaymentSucceededScreen)
      hide()
    } catch (error: any) {
      const errorMessage = getErrorMessage(error, t)
      captureException(error, {
        type: "network",
        target: "internal-api",
        category: "payment",
        step: "cancel-payment",
        provider: "stripe",
        context: {
          errorSource: "StripeCardPaymentForm - onBackClicked",
          errorMessage,
          orderId: order.order.id,
        },
      })
      showPaymentFailedScreen(errorMessage, "Stripe card")
    }
  }

  if (!stripe || !elements) {
    return null
  }

  const handleStartTimeout = () => {
    startTimeout(() => {
      // If userActionsDone is false, then the user is still in the process of confirming 3dsecure
      // Then we start another timeout to wait for the user to finish the process
      if (!userActionsDone) {
        clearTimeout(timeoutId.current)
        handleStartTimeout()
        // Returning true to stop cancellation
        return true
      }
      if (sseSubscriber) {
        sseSubscriber?.current?.close()
      }
    })
  }

  const confirmPayment = async () => {
    trackPaymentStage("stripe", "transaction-started", {
      orderId: order.order.id,
    })
    setPaymentProcessing(true)
    // Starting timeout
    handleStartTimeout()
    try {
      const card = elements.getElement("card")
      if (!card) {
        const error = new HeapsError({
          errorCode: "unknown_error",
          errorMessage: "Card element not found",
          translationKey: "",
        })
        captureException(error, {
          type: "business_logic",
          category: "invalid-state",
          context: {
            errorSource: "StripeCardPaymentForm - missing card element",
            provider: "stripe",
            orderId: order.order.id,
          },
        })
        throw error
      }
      await performStripeSCA(nextAction, stripe, {
        payment_method: {
          card,
        },
      })
      trackPaymentStage("stripe", "transaction-accepted", {
        orderId: order.order.id,
      })
      setUserActionsDone(true)
    } catch (error: any) {
      logCheckoutFailed(error)
      const hasCancelled = await handleCancelPayment(
        order.order.id,
        showPaymentSucceededScreen
      )
      const errorMessage = getErrorMessage(error, t)
      if (hasCancelled) {
        showPaymentFailedScreen(errorMessage, "Stripe card")
      }
    } finally {
      if (timeoutId.current) {
        clearTimeout(timeoutId.current)
        setTimeoutId(undefined)
      }
      setPaymentProcessing(false)
    }
  }

  return (
    <StripeCardForm
      buttonText={`Order and pay ${formatMoneyAmount(
        order.order.total_remaining_price.amount,
        order.order.total_remaining_price.currency,
        language
      )}`}
      goBack={onBackClicked}
      title={t(translationKeys.webPayment)}
      subtitle={t(translationKeys.webPaymentSubtitle)}
      onSubmit={confirmPayment}
      formSubmissionLoading={paymentProcessing}
    />
  )
}
